<template>
    <div class="[ pt-4 px-8 pb-8 ] [ bg-gray-pale ]">
        <div class="[ flex pt-0 min-h-[20vh] ]" v-if="allData">
            <div class="[ border-r w-1/4 grid grid-cols-2 grid-rows-2 ]">
                <div class="[ flex flex-col items-center justify-center ]" v-for="(stat, index) in allData.stats.value"
                    :class="{
                        '[ border-r ]': index % 2 !== 1,
                        '[ border-b ]': Math.floor(index / 2) < 1,
                    }">
                    <span class="[ text-5xl ]">{{ stat.value }}</span>
                    <span>{{ stat.text }}</span>
                </div>
            </div>
            <div class="[ flex flex-col flex-1 gap-2 ]">
                <div class="[ flex items-center justify-end p-2 ]">
                    <DateRangeRadio :range-options="dateRanges" :intervals="dateRangeIntervals"
                        :show-custom-datepicker="hasSelectedCustomDateRange" :custom-date-range="customDateRange"
                        v-model="dateRangeIndex" @custom-date-selected="setCustomDateFilter" />
                </div>
                <JBChart type="line" :data="allData.chartData.value" :options="allData.chartOptions.value"
                    :loading="allData.loadingTimeseries.value"
                    :class="{ 'opacity-50': allData.loadingTimeseries.value }" class="[ flex-1 ]" />
            </div>
        </div>
        <DataTable :value="filteredRows" class="[ bg-white ]" selection-mode="single" @row-select="onRowSelected"
            sort-mode="single" sort-field="dateCreated" :sort-order="-1" :rowClass="tableRowClass">
            <template #empty>
                <JBLoadingSpinner v-if="loading" />
                <Empty v-else-if="searchQueryDebounced.trim()" icon="unavailable" title="No results found"
                    message="Try a different search" />
                <Empty v-else icon="unavailable" title="No scorecards yet"
                    message="Create a new scorecard to get started">
                    <ButtonComponent label="New Scorecard" @click="navigateToNewScorecard"><template #icon>
                            <JBIcon name="add" />
                        </template>
                    </ButtonComponent>
                </Empty>
            </template>
            <Column header="ID" field="id" sortable>
                <template #body="{ data }: { data: ScorecardRow }">
                    <span class="font-mono text-sm">{{ data.id }}</span>
                </template>
            </Column>
            <Column header="Agent" field="agentName" sortable>
                <template #body="{ data }: { data: ScorecardRow }">
                    <div class="[ flex gap-2 ]">
                        <AgentAvatar
                            :user="{ id: data.agentId, firstName: data.agentFirstName, lastName: data.agentLastName }" />
                        <span>{{ data.agentFirstName }} {{ data.agentLastName }}</span>
                    </div>
                </template>
            </Column>
            <Column field="customerExternalId" header="Customer Id" />
            <Column header="Reviewer" field="reviewerName" sortable>
                <template #body="{ data }: { data: ScorecardRow }">
                    <div class="[ flex gap-2 ]">
                        <AgentAvatar
                            :user="{ id: data.reviewerId, firstName: data.reviewerFirstName, lastName: data.reviewerLastName }" />
                        <span>{{ data.reviewerFirstName }} {{ data.reviewerLastName }}</span>
                    </div>
                </template>
            </Column>
            <Column header="Date &amp; time" field="dateCreated" sortable>
                <template #body="{ data }: { data: ScorecardRow }">
                    <div class="[ flex gap-3 text-gray-dark ]">
                        <span class="[ border-r pr-3 ]">{{
                            readableDate(data.dateCreated!)
                        }}</span>
                        <span>{{ getTimeFromDate(data.dateCreated!) }}</span>
                    </div>
                </template>
            </Column>
            <Column header="Status">
                <template #body="{ data }: { data: ScorecardRow }">
                    <div class="text-xs">
                        <div v-if="data.status === ScorecardStatus.Processing">
                            <ProgressBar v-if="(typeof data.progress !== 'undefined')" :value="data.progress! * 100" />
                            <span v-else>{{ getScorecardStatusLabel(data.status, data.outcome) }}</span>
                        </div>
                        <div v-else>
                            <span>{{ getScorecardStatusLabel(data.status, data.outcome) }}</span>
                        </div>
                    </div>
                </template>
            </Column>
            <Column header="Score">
                <template #body="{ data }: { data: ScorecardRow }">
                    <ScoreProgressCircle :value="data.score!" v-if="(typeof data.score !== 'undefined')" />
                    <span v-else>-</span>
                </template>
            </Column>
            <Column header="Action">
                <template #body="{ data }: { data: ScorecardRow }">
                    <ButtonComponent outlined rounded class="[ !w-auto !py-2 !px-4 ]" @click="confirmDelete(data.id!)"
                        :loading="isDeletingId === data.id">
                        <template #icon>
                            <JBIcon name="trash" />
                        </template>
                    </ButtonComponent>
                </template>
            </Column>
        </DataTable>
        <Teleport to="[data-header-action]" v-if="isMounted">
            <div class="[ flex flex-row justify-between items-center flex-1 ] [ gap-4 ]">
                <div class="[ relative ] [ max-w-sm ]">
                    <InputText ref="searchInputRef" v-model="searchQuery" type="text"
                        class="[ text-sm ] [ w-full ] [ pr-10 ]" @keydown.enter="onSearchKeydownEnter($event)"
                        @keydown.esc="onSearchKeydownEsc($event)" />
                    <div class="[ absolute ] [ right-2 top-1/2 -translate-y-1/2 ] [ flex items-center ]"
                        :class="searchQuery ? '' : 'pointer-events-none'">
                        <button v-if="searchQuery" type="button" class="[ p-1 ] [ cursor-pointer ]"
                            aria-label="Clear search" @click="searchQuery = ''">
                            <JBIcon name="cross" class="[ w-4 ] [ h-4 ] [ text-gray-dark ]" />
                        </button>
                        <JBIcon v-else name="search" class="[ w-4 ] [ h-4 ] [ text-gray-dark ]" />
                    </div>
                </div>
                <ButtonComponent outlined label="New Scorecard" class="!border-green-accent"
                    @click="navigateToNewScorecard">
                    <template #icon>
                        <JBIcon name="add" />
                    </template>
                </ButtonComponent>
            </div>
        </Teleport>
    </div>

</template>
<script setup lang="ts">
import { definePage } from 'vue-router/auto'
import {
    Button as ButtonComponent,
    ProgressBar,
    DataTable,
    Column,
    DataTableRowSelectEvent,
    InputText,
} from "primevue";
import AgentAvatar from '@/components/agents/agent/AgentAvatar.vue';
import ScoreProgressCircle from '@/components/ui/ScoreProgressCircle.vue';
import { useApi, useApiLoader } from '@/services/api';
import { Scorecard, ScorecardStatus } from '@/services/api/sdk';
import { getTimeFromDate, readableDate } from '@/utils/date';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { isNavigationFailure, NavigationFailureType, useRouter } from 'vue-router';
import { useDebounceFn } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import Empty from '@/components/ui/Empty.vue';
import JBLoadingSpinner from '@/components/ui/JBLoadingSpinner.vue';
import { getScorecardStatusLabel } from '@/utils/status';
import { createWS, useConnection } from '@/services/ws';
import { IDisposable } from 'monaco-editor';
import { setBreadcrumbs } from '@/utils/breadcrumb';
import DateRangeRadio from '@/components/contact/qa/DateRangeRadio.vue';
import JBChart from '@/components/ui/JBChart.vue';
import JBIcon from '@/components/JBIcon.vue';
import {
    dateIntervals,
    dateRangeIntervals,
    dateRanges,
    getDateIntervalForCustomDateRange,
} from "@/utils/contacts/filters.ts";
import { useScorecardStats } from '@/composables/scorecard-stats';
import { useConfirm } from "primevue/useconfirm";
import { userHasPermission } from '@/composables/user-permissions';

const searchQuery = ref('');
const searchQueryDebounced = ref('');
const searchInputRef = ref<InstanceType<typeof InputText> | null>(null);
watch(searchQuery, useDebounceFn((v) => { searchQueryDebounced.value = v; }, 150), { immediate: true });

function blurSearchInput() {
    (searchInputRef.value as { $el?: HTMLInputElement } | null)?.$el?.blur();
}

function onSearchKeydownEnter(ev: KeyboardEvent) {
    ev.preventDefault();
    searchQueryDebounced.value = searchQuery.value;
    blurSearchInput();
}

function onSearchKeydownEsc(ev: KeyboardEvent) {
    ev.preventDefault();
    if (searchQuery.value) {
        blurSearchInput();
        searchQuery.value = '';
    } else {
        blurSearchInput();
    }
}
const dateRangeIndex = ref(0);
const customDateRange = ref<[Date | null, Date | null]>([null, null]);

const hasSelectedCustomDateRange = computed<boolean>(() => {
    const [start, end] = customDateRange.value;
    return !!(start && end);
});

const dateRange = computed(() => {
    if (hasSelectedCustomDateRange.value) {
        return customDateRange.value;
    }
    return dateRanges[dateRangeIndex.value];
});
const dateInterval = computed(() => {
    if (hasSelectedCustomDateRange.value) {
        return getDateIntervalForCustomDateRange(customDateRange.value!);
    }
    return dateIntervals[dateRangeIndex.value];
});

function setCustomDateFilter(
    v: Date | (Date | null)[] | Date[] | null | undefined,
) {
    customDateRange.value = v as [Date, Date];
}

const allData = useScorecardStats({ dateRange, dateInterval });

const showingScorecard = ref(false);
const isMounted = ref<boolean>(false);

const router = useRouter();

const selectedScorecardId = useRouteQuery('scorecardId');

watch(showingScorecard, () => {
    if (!showingScorecard.value) {
        selectedScorecardId.value = null;
    }
});

const api = useApi();

function navigateToNewScorecard() {
    router.push('/scorecards/scorecard');
}

function tableRowClass(row: Scorecard) {
    if (row.status === ScorecardStatus.Processing) {
        return 'opacity-50 !cursor-default !hover:bg-none';
    }
    // Users without ScorecardTemplate.Write permission can't click Processed rows
    if (row.status === ScorecardStatus.Processed && !userHasPermission("ScorecardTemplate.Write")) {
        return '!cursor-default';
    }
    return '';
}

const { load: loadScorecards, data: scorecards, loading } = useApiLoader(async () => {
    const res = await api.listScorecards();

    updateListeners(res.data!);

    return res.data;
}, null);

function replaceScorecard(scorecard: Scorecard) {
    if (!scorecards.value) {
        return;
    }
    const index = scorecards.value.findIndex(x => x.id === scorecard.id);
    if (index === -1) {
        return;
    }
    scorecards.value.splice(index, 1, scorecard);
}

let listeners: IDisposable | null = null;

const { getConnection } = useConnection();

async function updateListeners(scorecards: Scorecard[]) {
    listeners?.dispose();
    const connection = await getConnection();
    const ws = createWS(connection);
    listeners = await ws.setupListeners(() => {
        for (const scorecard of scorecards) {
            if (scorecard.status === ScorecardStatus.Processing) {
                ws.onScorecardUpdated(scorecard.id!, (updated) => {
                    replaceScorecard(updated);
                });
            }
        }
    });
}

onUnmounted(() => {
    listeners?.dispose();
})

type ScorecardRow = Scorecard & {
    agentName: string;
    reviewerName: string;
};

const rows = computed<ScorecardRow[]>(() => {
    const list = scorecards.value ?? [];
    return list.map((sc) => ({
        ...sc,
        agentName: `${sc.agentFirstName ?? ''} ${sc.agentLastName ?? ''}`.trim(),
        reviewerName: `${sc.reviewerFirstName ?? ''} ${sc.reviewerLastName ?? ''}`.trim(),
    }));
});

const filteredRows = computed<ScorecardRow[]>(() => {
    const query = searchQueryDebounced.value.trim().toLowerCase();
    if (!query) {
        return rows.value;
    }

    // Filter rows by search input text
    return rows.value.filter((row) => {
        const searchableText = [
            String(row.id ?? ''),
            row.agentName ?? '',
            row.reviewerName ?? '',
            row.customerExternalId ?? '',
        ].join(' ').toLowerCase();
        return searchableText.includes(query);
    });
});

function onRowSelected(ev: DataTableRowSelectEvent) {
    if (ev.data.status === ScorecardStatus.Ready || ev.data.status === ScorecardStatus.Completed || (ev.data.status === ScorecardStatus.Processed && userHasPermission("ScorecardTemplate.Write"))) {
        openScorecard(ev.data.id);
    }
}

async function openScorecard(id: number) {
    try {
        await router.push({
            name: 'scorecard-result',
            params: {
                id,
            },
        });
    } catch (e) {
        if (isNavigationFailure(e, NavigationFailureType.duplicated)) {
            return;
        }
        console.error('Failed to navigate to scorecard result', e);
    }
}

const confirm = useConfirm();
const isDeletingId = ref<number | null>(null);
const selectedScorecardForDeletion = ref<number | null>(null);

function confirmDelete(scorecardId: number) {
    selectedScorecardForDeletion.value = scorecardId;
    confirm.require({
        message: 'Are you sure you want to remove this scorecard?',
        header: 'Confirm',
        icon: 'pi pi-exclamation-triangle',
        accept: async () => {
            await removeScorecard(scorecardId);
        },
    });
}

const { load: deleteScorecard } = useApiLoader(async () => {
    if (!selectedScorecardForDeletion.value) {
        return null;
    }
    await api.deleteScorecard({
        scorecardId: selectedScorecardForDeletion.value,
    });
    return null;
}, null);

async function removeScorecard(scorecardId: number) {
    isDeletingId.value = scorecardId;
    try {
        selectedScorecardForDeletion.value = scorecardId;
        await deleteScorecard();
        // Remove from local list
        if (scorecards.value) {
            scorecards.value = scorecards.value.filter(sc => sc.id !== scorecardId);
        }
    } finally {
        isDeletingId.value = null;
        selectedScorecardForDeletion.value = null;
    }
}

onMounted(() => {
    isMounted.value = true;
    setBreadcrumbs({
        crumbs: [],
        trailingTitle: "All scorecards",
    });
    loadScorecards();
});

definePage({
    name: "scorecards",
    meta: {
        title: "Scorecards",
        icon: "hierarchy-square",
        noPadding: true
    },
});
</script>
